import { whyChooseUs } from '../../../mocks/services';

export default function WhyChooseUsSection() {
  return (
    <section id="why-choose-us" className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-[#0a1628] via-[#162447] to-[#0a1628] text-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-96 h-96 bg-cyan-400 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-400 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 sm:mb-10 lg:mb-12">
          <h2 className="text-3xl sm:text-4xl font-black mb-3 sm:mb-4">
            We Find Leaks Fast. We Fix Them Right. Same Day.
          </h2>
          <p className="text-base sm:text-xl text-blue-200 max-w-3xl mx-auto">
            Every hour a leak goes undetected, it gets more expensive. At Leak Detectives, we use professional equipment and real field experience to pinpoint the problem accurately — then fix it the same day with flat-rate pricing.
          </p>
        </div>

        {/* Mobile: Horizontal icon + text layout */}
        <div className="flex flex-col gap-5 mb-8 sm:mb-10 lg:hidden">
          {whyChooseUs.map((item) => (
            <div key={item.id} className="bg-white/10 backdrop-blur-sm rounded-xl p-5 flex items-start gap-4 hover:bg-white/20 transition-all duration-300">
              <div className="w-16 h-16 flex items-center justify-center bg-cyan-500 rounded-xl flex-shrink-0">
                <i className={`${item.icon} text-3xl text-white`}></i>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                <p className="text-blue-200 text-base leading-relaxed">{item.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop: Original 3-column grid */}
        <div className="hidden lg:grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 mb-10 sm:mb-12">
          {whyChooseUs.map((item) => (
            <div key={item.id} className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 sm:p-8 text-center hover:bg-white/20 transition-all duration-300">
              <div className="w-20 h-20 flex items-center justify-center bg-cyan-500 rounded-2xl mx-auto mb-6">
                <i className={`${item.icon} text-4xl text-white`}></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold mb-4">{item.title}</h3>
              <p className="text-blue-200 text-sm sm:text-base">{item.description}</p>
            </div>
          ))}
        </div>

        {/* Desktop: Image + Checklist */}
        <div className="hidden lg:grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          <div>
            <img
              src="https://public.readdy.ai/ai/img_res/2751a919ef988ffaeda946509f5a91ca.jpg"
              alt="Leak Detectives Professional Team Las Vegas"
              className="rounded-2xl shadow-2xl w-full h-64 object-cover object-top"
            />
          </div>
          <div>
            <h3 className="text-2xl sm:text-3xl font-bold mb-6">Same-Day Leak Detection &amp; Immediate Repairs</h3>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 flex items-center justify-center bg-cyan-500 rounded-lg flex-shrink-0 mt-1">
                  <i className="ri-check-line text-xl text-white"></i>
                </div>
                <div>
                  <h4 className="font-bold text-lg sm:text-xl mb-1">Diagnose First, Fix Same Day</h4>
                  <p className="text-blue-200 text-sm sm:text-base">We find the root cause before we fix anything — then complete same-day repairs so you&apos;re not left waiting.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 flex items-center justify-center bg-cyan-500 rounded-lg flex-shrink-0 mt-1">
                  <i className="ri-check-line text-xl text-white"></i>
                </div>
                <div>
                  <h4 className="font-bold text-lg sm:text-xl mb-1">Flat-Rate Pricing — No Surprises</h4>
                  <p className="text-blue-200 text-sm sm:text-base">No hourly billing. No inflated invoices. You&apos;ll know the exact cost before we start — free, no-obligation estimate every time.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 flex items-center justify-center bg-cyan-500 rounded-lg flex-shrink-0 mt-1">
                  <i className="ri-check-line text-xl text-white"></i>
                </div>
                <div>
                  <h4 className="font-bold text-lg sm:text-xl mb-1">Call &amp; Speak to a Real Plumber Now</h4>
                  <p className="text-blue-200 text-sm sm:text-base">When you call, you speak directly to a certified plumber — not a receptionist. Get immediate answers and same-day service.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 flex items-center justify-center bg-cyan-500 rounded-lg flex-shrink-0 mt-1">
                  <i className="ri-check-line text-xl text-white"></i>
                </div>
                <div>
                  <h4 className="font-bold text-lg sm:text-xl mb-1">Complete Service — Detection to Repair</h4>
                  <p className="text-blue-200 text-sm sm:text-base">We handle everything from locating the leak to completing the repair. One call stops the damage for good.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile: Checklist only (no image) */}
        <div className="lg:hidden">
          <h3 className="text-2xl font-bold mb-6">Same-Day Leak Detection &amp; Immediate Repairs</h3>
          <div className="space-y-5">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center bg-cyan-500 rounded-lg flex-shrink-0 mt-1">
                <i className="ri-check-line text-2xl text-white"></i>
              </div>
              <div>
                <h4 className="font-bold text-xl mb-2">Diagnose First, Fix Same Day</h4>
                <p className="text-blue-200 text-base leading-relaxed">We find the root cause before we fix anything — then complete same-day repairs so you&apos;re not left waiting.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center bg-cyan-500 rounded-lg flex-shrink-0 mt-1">
                <i className="ri-check-line text-2xl text-white"></i>
              </div>
              <div>
                <h4 className="font-bold text-xl mb-2">Flat-Rate Pricing — No Surprises</h4>
                <p className="text-blue-200 text-base leading-relaxed">No hourly billing. No inflated invoices. Free, no-obligation estimate before any work begins.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center bg-cyan-500 rounded-lg flex-shrink-0 mt-1">
                <i className="ri-check-line text-2xl text-white"></i>
              </div>
              <div>
                <h4 className="font-bold text-xl mb-2">Call &amp; Speak to a Real Plumber Now</h4>
                <p className="text-blue-200 text-base leading-relaxed">When you call, you speak directly to a certified plumber — immediate answers, same-day service available.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center bg-cyan-500 rounded-lg flex-shrink-0 mt-1">
                <i className="ri-check-line text-2xl text-white"></i>
              </div>
              <div>
                <h4 className="font-bold text-xl mb-2">Complete Service — Detection to Repair</h4>
                <p className="text-blue-200 text-base leading-relaxed">We handle everything from locating the leak to completing the repair. One call stops the damage for good.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Urgency CTA Bar */}
        <div className="mt-10 sm:mt-12 bg-red-600/20 border border-red-500/40 rounded-2xl px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-white font-bold text-lg sm:text-xl text-center sm:text-left">
            Leaks don&apos;t fix themselves — <span className="text-red-300">the longer you wait, the more it costs.</span>
          </p>
          <a
            href="tel:7252675482"
            className="bg-red-600 text-white font-black text-base sm:text-lg px-8 py-4 rounded-lg hover:bg-red-500 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2 flex-shrink-0"
          >
            <i className="ri-phone-fill text-xl"></i>
            Call Now
          </a>
        </div>
      </div>
    </section>
  );
}